import { Fragment, useState } from "react";
import PropTypes from "prop-types";
import { compose } from "redux";
import { accountingConnector } from "../../../../services/accounting/connector";

import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import CardActions from "@material-ui/core/CardActions";
import TextField from "@material-ui/core/TextField";

import { Button } from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers/DatePicker";

import xlsx from "xlsx";
import { moneyFormater } from "../../../../utilities/formaters";

import "./InvestorDepositImport.scss";

const InvestorDepositImport = ({ registerPayment, attachIntervals }) => {
  const [deposits, setDeposits] = useState([]);
  const [enableRegisterButton, setEnableRegisterButton] = useState(false);
  const [bankReceiveDate, setBankReceiveDate] = useState(new Date());
  const [totalDepositsCount, setTotalDepositsCount] = useState(0);
  const [totalDepositsAmount, setTotalDepositsAmount] = useState(0);

  return (
    <div className="investor-deposit-import">
      <Card>
        <CardHeader
          title="Register Investor Deposits"
          subheader={"Import investor deposits from excelfile"}
        />
        <CardContent>
          {deposits.length > 0 && (
            <div>
              <div className="import-information">
                <DatePicker
                  inputVariant="outlined"
                  className="small-datepicker"
                  value={bankReceiveDate}
                  onChange={setBankReceiveDate}
                  fullWidth={false}
                  label="Bank registered date"
                />
                <div>
                  <div>Deposits: {totalDepositsCount}</div>
                  <div>
                    Total Amount: {moneyFormater(totalDepositsAmount, 2)}
                  </div>
                </div>
              </div>
              <div className="investor-deposit-grid">
                <div className="heading">Name</div>
                <div className="heading">Reference Code</div>
                <div className="heading">Payment Id</div>
                <div className="heading">Amount</div>
                {deposits.map((deposit, index) => {
                  return (
                    <Fragment key={index}>
                      <div>{deposit.InvestorName}</div>
                      <TextField
                        size="small"
                        variant="outlined"
                        value={deposit.ReferenceCode}
                        onChange={(event) => {
                          changeImportDepositReferenceCode(index, event);
                        }}
                        fullWidth={true}
                        label="Reference Code"
                        error={!deposit.HasValidReferenceCode}
                      />
                      <TextField
                        size="small"
                        variant="outlined"
                        value={deposit.PaymentId}
                        onChange={(event) => {
                          changeImportDepositPaymentId(index, event);
                        }}
                        fullWidth={true}
                        label="PaymentId"
                        error={!deposit.HasValidPaymentId}
                      />
                      <div>{moneyFormater(deposit.Amount, 2)}</div>
                    </Fragment>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
        <CardActions>
          <Button
            component="label"
            variant="contained"
            color="primary"
            id="importInvestorDeposits"
          >
            <input
              id="fileImportInput"
              type="file"
              className="hidden-input"
              onChange={importInvestorDeposits}
            />
            Import Investor Deposits
          </Button>
          <Button
            variant="contained"
            color="primary"
            id="registerInvestorDeposits"
            onClick={registerInvestorDeposits}
            disabled={!enableRegisterButton}
          >
            Register Investor Deposits
          </Button>
        </CardActions>
      </Card>
    </div>
  );

  function changeDeposits(updatedDeposits) {
    setDeposits([...updatedDeposits]);
    setEnableRegisterButton(validateDeposits(updatedDeposits));
  }

  function changeImportDepositReferenceCode(depositIndex, event) {
    let updatedDeposits = deposits;
    let deposit = updatedDeposits[depositIndex];
    deposit.ReferenceCode = event.target.value;
    deposit.HasValidReferenceCode = validateReferenceCode(
      deposit.ReferenceCode,
    );

    changeDeposits(updatedDeposits);
  }

  function changeImportDepositPaymentId(depositIndex, event) {
    let updatedDeposits = deposits;
    let deposit = updatedDeposits[depositIndex];
    deposit.PaymentId = event.target.value;
    deposit.HasValidPaymentId = validatePaymentId(deposit.PaymentId);

    // PaymentIds are unique and must all be validated when an update occurs
    validatePaymentIds(updatedDeposits);

    changeDeposits(updatedDeposits);
  }

  function validatePaymentIds(updatedDeposits) {
    updatedDeposits.forEach((deposit) => {
      const isDuplicate =
        updatedDeposits.filter((x) => x.PaymentId === deposit.PaymentId)
          .length > 1;

      deposit.HasValidPaymentId =
        !isDuplicate && validatePaymentId(deposit.PaymentId);
    });
  }

  function validateDeposits(updatedDeposits) {
    if (updatedDeposits.length <= 0) return false;

    if (!updatedDeposits.every((x) => x.HasValidReferenceCode)) return false;

    if (!updatedDeposits.every((x) => x.HasValidPaymentId)) return false;

    if (!validateDepositsTotalCount()) return false;

    if (!validateDepositsTotalAmount()) return false;

    return true;
  }

  function validateReferenceCode(depositReferenceCode) {
    return depositReferenceCode.toString().match(/^([\d]){12,13}$/);
  }

  function validatePaymentId(depositPaymentId) {
    return depositPaymentId.toString().match(/^([\d]){12}$/);
  }

  function validateDepositsTotalCount() {
    return deposits.length === totalDepositsCount;
  }

  function validateDepositsTotalAmount() {
    const sum = deposits.reduce((a, b) => a + b.Amount * 100, 0) / 100; // *100 to avoid float issues, then /100 back again
    return sum === totalDepositsAmount;
  }

  function registerInvestorDeposits() {
    deposits.forEach((deposit) => {
      registerInvestorDeposit(deposit);
    });

    changeDeposits([]);
  }

  function registerInvestorDeposit(deposit) {
    const data = {
      SenderName: deposit.InvestorName,
      PaymentId: Number(deposit.PaymentId),
      Amount: Number(deposit.Amount),
      RefCode: Number(deposit.ReferenceCode),
      BankReceiveDate: bankReceiveDate,
    };
    registerPayment(data, "investor_deposit");
    attachIntervals();
  }

  function importInvestorDeposits(event) {
    const file = event.target.files[0];
    let depositOptions = {
      header: "",
      raw: true,
    };

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target.result;
      const workbook = xlsx.read(data, { type: "binary" });
      const workSheet = workbook.Sheets[workbook.SheetNames[0]];
      for (let cell in workSheet) {
        if (workSheet[cell].v === "Sorterad på belopp") {
          depositOptions.range = Number(cell.substring(1));
        }
      }

      setBankReceiveDate(workSheet.A5.v);
      let totalDeposits = 0;
      let totalAmount = 0;

      const depositsJson = xlsx.utils.sheet_to_json(workSheet, depositOptions);
      let newDeposits = [];

      for (let deposit of depositsJson) {
        const paymentId = deposit["Bankgironummer/\r\nAvinummer"];
        const refCode = deposit["Betalningsreferens"];
        const amount = deposit["Belopp"]
          .toString()
          // Remove all formatting cases
          .replace(" ", "")
          .replace(",", ".");

        // There are some problems with the content of the excel file
        // it adds some empty rows with description of the file so we ignore any row
        // not including either an ocr, payment id or an amount

        if (paymentId || refCode || amount) {
          newDeposits.push({
            InvestorName: deposit["Avsändare"],
            PaymentId: paymentId,
            ReferenceCode: refCode,
            HasValidReferenceCode: validateReferenceCode(refCode),
            Amount: Number(amount),
          });

          totalDeposits++;
          totalAmount += Number(amount);
        }
      }

      setTotalDepositsCount(totalDeposits);
      setTotalDepositsAmount(totalAmount);

      // PaymentIds are unique and must all be validated when an update occurs
      validatePaymentIds(newDeposits);

      changeDeposits(newDeposits);
    };

    reader.readAsBinaryString(file);
  }
};

InvestorDepositImport.propTypes = {
  registerPayment: PropTypes.func,
  attachIntervals: PropTypes.func,
};

export default compose(accountingConnector)(InvestorDepositImport);
